/**
 * LexDuo · F2/F3 Ejecuciones · Editor de plantillas de email
 * Modal con tabla de plantillas + editor (asunto + cuerpo HTML enriquecido).
 * GET  /api/ejecuciones/plantillas/list
 * PUT  /api/ejecuciones/plantillas/:codigo
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Loader2, Save, Mail, Eye, RotateCcw, FileText } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import RichTextEditor from '../RichTextEditor';
import { obtenerToken } from '../../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ejecuciones/plantillas`;
const headers = () => {
  const tok = obtenerToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const PlantillasEmailEditor = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [activa, setActiva] = useState(null);
  const [draft, setDraft] = useState({ asunto: '', cuerpo_html: '', nombre: '' });

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/list`, { headers: headers() });
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setPlantillas(items);
      if (items.length && !activa) {
        setActiva(items[0].codigo);
        setDraft({ asunto: items[0].asunto, cuerpo_html: items[0].cuerpo_html, nombre: items[0].nombre });
      }
    } catch (e) {
      toast({ title: 'Error cargando plantillas', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) cargar(); /* eslint-disable-next-line */ }, [open]);

  const seleccionar = (codigo) => {
    const p = plantillas.find(x => x.codigo === codigo);
    if (!p) return;
    setActiva(codigo);
    setDraft({ asunto: p.asunto, cuerpo_html: p.cuerpo_html, nombre: p.nombre });
  };

  const guardar = async () => {
    if (!activa) return;
    setSaving(true);
    try {
      await axios.put(`${API}/${activa}`, {
        nombre: draft.nombre,
        asunto: draft.asunto,
        cuerpo_html: draft.cuerpo_html,
      }, { headers: headers() });
      toast({ title: 'Plantilla guardada' });
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const restaurar = () => {
    const p = plantillas.find(x => x.codigo === activa);
    if (p) setDraft({ asunto: p.asunto, cuerpo_html: p.cuerpo_html, nombre: p.nombre });
  };

  const plantillaActiva = plantillas.find(x => x.codigo === activa);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="dialog-plantillas-email">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-violet-700" /> Editor de plantilles d&apos;email · Execucions
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-700" /></div>
        ) : plantillas.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm">No hi ha plantilles disponibles. Apliqueu la migració SQL.</div>
        ) : (
          <Tabs value={activa || plantillas[0]?.codigo} onValueChange={seleccionar} className="mt-2">
            <TabsList className="w-full justify-start flex-wrap">
              {plantillas.map(p => (
                <TabsTrigger key={p.codigo} value={p.codigo} data-testid={`tab-plantilla-${p.codigo}`}>
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> {p.nombre || p.codigo}
                </TabsTrigger>
              ))}
            </TabsList>

            {plantillas.map(p => (
              <TabsContent key={p.codigo} value={p.codigo} className="space-y-3 mt-4">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs">{p.codigo}</Badge>
                  {p.activo && <Badge className="bg-emerald-100 text-emerald-800 text-xs">activa</Badge>}
                </div>

                <div>
                  <Label className="text-xs">Nom intern</Label>
                  <Input value={draft.nombre} onChange={(e) => setDraft({ ...draft, nombre: e.target.value })} data-testid="input-nombre-plantilla" />
                </div>

                <div>
                  <Label className="text-xs">Assumpte</Label>
                  <Input value={draft.asunto} onChange={(e) => setDraft({ ...draft, asunto: e.target.value })} data-testid="input-asunto-plantilla" />
                </div>

                <div>
                  <Label className="text-xs flex items-center justify-between">
                    <span>Cos del missatge (HTML enriquit)</span>
                    <span className="text-[10px] text-gray-500">
                      Variables disponibles: <code className="bg-gray-100 px-1">{p.variables_disponibles || '—'}</code>
                    </span>
                  </Label>
                  <div className="border rounded-md mt-1">
                    <RichTextEditor
                      value={draft.cuerpo_html}
                      onChange={(html) => setDraft({ ...draft, cuerpo_html: html })}
                      placeholder="Escriviu el cos del correu..."
                    />
                  </div>
                </div>

                <div className="bg-gray-50 border rounded-md p-3">
                  <div className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5" /> Vista prèvia (sense reemplaçar variables)
                  </div>
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: draft.cuerpo_html }} />
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={restaurar} disabled={saving} data-testid="btn-restaurar-plantilla">
            <RotateCcw className="w-4 h-4 mr-1" /> Descartar canvis
          </Button>
          <Button variant="outline" onClick={onClose}>Tancar</Button>
          <Button onClick={guardar} disabled={saving || !activa} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-guardar-plantilla">
            {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Desant…</> : <><Save className="w-4 h-4 mr-1" /> Desar plantilla</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlantillasEmailEditor;
